import React, { useEffect, useMemo, useState } from 'react';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';

type JobStatus = 'queued' | 'running' | 'done' | 'error';

type BackupJob = {
  id: string;
  status: JobStatus;
  progress: number;
  size?: number;
  error?: string;
};

type ManifestInfo = {
  backupType: 'portal' | 'full';
  createdAt: string;
  systemVersion: string;
  dbEngine?: string;
  hash: string;
  size: number;
};

const formatBytes = (value?: number) => {
  if (!value || value <= 0) return '—';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const idx = Math.min(Math.floor(Math.log(value) / Math.log(1024)), units.length - 1);
  const scaled = value / Math.pow(1024, idx);
  const digits = scaled >= 10 || idx === 0 ? 0 : 1;
  return `${scaled.toFixed(digits)} ${units[idx]}`;
};

const BackupRestore: React.FC = () => {
  const { user } = useAuth();
  const [portalJob, setPortalJob] = useState<BackupJob | null>(null);
  const [fullJob, setFullJob] = useState<BackupJob | null>(null);
  const [loadingPortal, setLoadingPortal] = useState(false);
  const [loadingFull, setLoadingFull] = useState(false);

  const [portalFile, setPortalFile] = useState<File | null>(null);
  const [fullFile, setFullFile] = useState<File | null>(null);
  const [portalManifest, setPortalManifest] = useState<ManifestInfo | null>(null);
  const [fullManifest, setFullManifest] = useState<ManifestInfo | null>(null);
  const [portalRestoreId, setPortalRestoreId] = useState<string | null>(null);
  const [fullRestoreId, setFullRestoreId] = useState<string | null>(null);
  const [portalLogs, setPortalLogs] = useState<string[]>([]);
  const [fullLogs, setFullLogs] = useState<string[]>([]);
  const [confirmTextPortal, setConfirmTextPortal] = useState('');
  const [confirmTextFull, setConfirmTextFull] = useState('');
  const [errorPortal, setErrorPortal] = useState('');
  const [errorFull, setErrorFull] = useState('');

  const isAdmin = user?.role === 'GLOBAL_ADMIN';

  const pollJob = async (jobId: string, setJob: (job: BackupJob) => void) => {
    const res = await api.get(`/admin/backup/jobs/${jobId}`);
    setJob(res.data);
    return res.data as BackupJob;
  };

  const pollRestore = async (restoreId: string, setLogs: (logs: string[]) => void) => {
    const res = await api.get(`/admin/backup/restore/${restoreId}`);
    setLogs(res.data?.logs || []);
    return res.data as { status: JobStatus; error?: string };
  };

  useEffect(() => {
    let timer: any;
    if (portalJob && portalJob.status === 'running') {
      timer = setInterval(async () => {
        const data = await pollJob(portalJob.id, setPortalJob);
        if (data.status === 'done' || data.status === 'error') clearInterval(timer);
      }, 2000);
    }
    return () => clearInterval(timer);
  }, [portalJob]);

  useEffect(() => {
    let timer: any;
    if (fullJob && fullJob.status === 'running') {
      timer = setInterval(async () => {
        const data = await pollJob(fullJob.id, setFullJob);
        if (data.status === 'done' || data.status === 'error') clearInterval(timer);
      }, 2000);
    }
    return () => clearInterval(timer);
  }, [fullJob]);

  useEffect(() => {
    let timer: any;
    if (portalRestoreId) {
      timer = setInterval(async () => {
        const data = await pollRestore(portalRestoreId, setPortalLogs);
        if (data.status === 'done' || data.status === 'error') clearInterval(timer);
      }, 2000);
    }
    return () => clearInterval(timer);
  }, [portalRestoreId]);

  useEffect(() => {
    let timer: any;
    if (fullRestoreId) {
      timer = setInterval(async () => {
        const data = await pollRestore(fullRestoreId, setFullLogs);
        if (data.status === 'done' || data.status === 'error') clearInterval(timer);
      }, 2000);
    }
    return () => clearInterval(timer);
  }, [fullRestoreId]);

  const handleGeneratePortal = async () => {
    setErrorPortal('');
    setLoadingPortal(true);
    try {
      const res = await api.post('/admin/backup/portal');
      const jobId = res.data?.jobId;
      if (jobId) {
        setPortalJob({ id: jobId, status: 'running', progress: 0 });
      }
    } catch (error: any) {
      setErrorPortal(error?.response?.data?.error || 'Falha ao gerar backup.');
    } finally {
      setLoadingPortal(false);
    }
  };

  const handleGenerateFull = async () => {
    setErrorFull('');
    setLoadingFull(true);
    try {
      const res = await api.post('/admin/backup/full');
      const jobId = res.data?.jobId;
      if (jobId) {
        setFullJob({ id: jobId, status: 'running', progress: 0 });
      }
    } catch (error: any) {
      setErrorFull(error?.response?.data?.error || 'Falha ao gerar backup.');
    } finally {
      setLoadingFull(false);
    }
  };

  const handleDownload = async (job?: BackupJob | null) => {
    if (!job) return;
    try {
      const response = await api.get(`/admin/backup/jobs/${job.id}/download`, {
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `backup-${job.id}.zip`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      if (job.id === portalJob?.id) {
        setErrorPortal(error?.response?.data?.error || 'Falha no download.');
      } else if (job.id === fullJob?.id) {
        setErrorFull(error?.response?.data?.error || 'Falha no download.');
      }
    }
  };

  const validateBackup = async (file: File, setManifest: (info: ManifestInfo | null) => void, setError: (msg: string) => void) => {
    setError('');
    setManifest(null);
    const form = new FormData();
    form.append('file', file);
    try {
      const res = await api.post('/admin/backup/validate', form, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setManifest(res.data?.manifest || null);
    } catch (error: any) {
      setError(error?.response?.data?.error || 'Backup inválido.');
    }
  };

  const restoreBackup = async (
    file: File,
    mode: 'portal' | 'full',
    confirmText: string,
    setRestoreId: (id: string | null) => void,
    setLogs: (logs: string[]) => void,
    setError: (msg: string) => void
  ) => {
    setError('');
    const form = new FormData();
    form.append('file', file);
    form.append('mode', mode);
    form.append('confirmText', confirmText);
    try {
      const res = await api.post('/admin/backup/restore', form, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setRestoreId(res.data?.restoreId || null);
      setLogs([]);
    } catch (error: any) {
      setError(error?.response?.data?.error || 'Falha ao restaurar.');
    }
  };

  const portalReady = useMemo(() => portalJob?.status === 'done', [portalJob]);
  const fullReady = useMemo(() => fullJob?.status === 'done', [fullJob]);

  if (!isAdmin) {
    return <p className="text-sm text-gray-500">Acesso restrito.</p>;
  }

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-xl font-semibold">Backup & Restauração</h2>
        <p className="text-sm text-gray-500 mt-1">Gerencie backups do portal e do banco de dados.</p>
      </div>

      <section className="space-y-4">
        <h3 className="text-lg font-semibold">Seção A — Gerar Backup (Download)</h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="bg-white rounded-lg shadow p-5 space-y-3">
            <div>
              <h4 className="text-md font-semibold">Backup do Portal</h4>
              <p className="text-sm text-gray-500">Inclui cadastros e configurações do portal.</p>
            </div>
            <button
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:bg-gray-400"
              onClick={handleGeneratePortal}
              disabled={loadingPortal || portalJob?.status === 'running'}
            >
              {loadingPortal || portalJob?.status === 'running' ? 'Gerando...' : 'Gerar e baixar'}
            </button>
            {portalJob && (
              <div className="text-sm text-gray-500 space-y-1">
                <p>Status: {portalJob.status}</p>
                <p>Progresso: {portalJob.progress || 0}%</p>
                <p>Tamanho: {formatBytes(portalJob.size)}</p>
                {portalReady && (
                  <button className="text-blue-600 underline" onClick={() => handleDownload(portalJob)}>
                    Baixar agora
                  </button>
                )}
                {portalJob.error && <p className="text-red-600">{portalJob.error}</p>}
              </div>
            )}
            {errorPortal && <p className="text-sm text-red-600">{errorPortal}</p>}
          </div>

          <div className="bg-white rounded-lg shadow p-5 space-y-3">
            <div>
              <h4 className="text-md font-semibold">Backup Completo (Banco de Dados)</h4>
              <p className="text-sm text-gray-500">Inclui dump completo do banco de dados.</p>
              <p className="text-xs text-amber-600 mt-2">Pode levar alguns minutos e o arquivo pode ser grande.</p>
            </div>
            <button
              className="bg-gray-900 text-white px-4 py-2 rounded-lg hover:bg-black disabled:bg-gray-400"
              onClick={handleGenerateFull}
              disabled={loadingFull || fullJob?.status === 'running'}
            >
              {loadingFull || fullJob?.status === 'running' ? 'Gerando...' : 'Gerar e baixar (Completo)'}
            </button>
            {fullJob && (
              <div className="text-sm text-gray-500 space-y-1">
                <p>Status: {fullJob.status}</p>
                <p>Progresso: {fullJob.progress || 0}%</p>
                <p>Tamanho: {formatBytes(fullJob.size)}</p>
                {fullReady && (
                  <button className="text-blue-600 underline" onClick={() => handleDownload(fullJob)}>
                    Baixar agora
                  </button>
                )}
                {fullJob.error && <p className="text-red-600">{fullJob.error}</p>}
              </div>
            )}
            {errorFull && <p className="text-sm text-red-600">{errorFull}</p>}
          </div>
        </div>
      </section>

      <section className="space-y-4">
        <h3 className="text-lg font-semibold">Seção B — Restaurar Backup (Upload)</h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="bg-white rounded-lg shadow p-5 space-y-4">
            <div>
              <h4 className="text-md font-semibold">Restaurar Backup do Portal</h4>
              <p className="text-sm text-gray-500">Use apenas arquivos do tipo portal.</p>
            </div>
            <input
              type="file"
              accept=".zip"
              onChange={(e) => setPortalFile(e.target.files?.[0] || null)}
            />
            <div className="flex gap-2">
              <button
                className="border px-4 py-2 rounded-lg hover:bg-gray-50"
                disabled={!portalFile}
                onClick={() =>
                  portalFile && validateBackup(portalFile, setPortalManifest, setErrorPortal)
                }
              >
                Validar Backup
              </button>
            </div>
            {portalManifest && (
              <div className="text-sm text-gray-600 space-y-1">
                <p>Tipo: {portalManifest.backupType}</p>
                <p>Versão: {portalManifest.systemVersion}</p>
                <p>Criado em: {portalManifest.createdAt}</p>
                <p>Checksum: {portalManifest.hash}</p>
                <p>Tamanho: {formatBytes(portalManifest.size)}</p>
              </div>
            )}
            <div className="border rounded-lg p-3 bg-amber-50 text-sm text-amber-700">
              Essa ação irá sobrescrever dados. Confirmar?
            </div>
            <input
              type="text"
              placeholder="Digite RESTORE para confirmar"
              value={confirmTextPortal}
              onChange={(e) => setConfirmTextPortal(e.target.value)}
              className="border rounded-lg px-3 py-2 w-full"
            />
            <button
              className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 disabled:bg-gray-400"
              disabled={!portalFile || confirmTextPortal !== 'RESTORE'}
              onClick={() =>
                portalFile &&
                restoreBackup(
                  portalFile,
                  'portal',
                  confirmTextPortal,
                  setPortalRestoreId,
                  setPortalLogs,
                  setErrorPortal
                )
              }
            >
              Restaurar
            </button>
            {portalLogs.length > 0 && (
              <div className="text-xs bg-gray-100 rounded p-3 max-h-40 overflow-auto">
                {portalLogs.map((log, idx) => (
                  <div key={idx}>{log}</div>
                ))}
              </div>
            )}
            {errorPortal && <p className="text-sm text-red-600">{errorPortal}</p>}
          </div>

          <div className="bg-white rounded-lg shadow p-5 space-y-4">
            <div>
              <h4 className="text-md font-semibold">Restaurar Backup Completo</h4>
              <p className="text-sm text-gray-500">Use apenas arquivos do tipo full.</p>
            </div>
            <input
              type="file"
              accept=".zip"
              onChange={(e) => setFullFile(e.target.files?.[0] || null)}
            />
            <div className="flex gap-2">
              <button
                className="border px-4 py-2 rounded-lg hover:bg-gray-50"
                disabled={!fullFile}
                onClick={() => fullFile && validateBackup(fullFile, setFullManifest, setErrorFull)}
              >
                Validar Backup
              </button>
            </div>
            {fullManifest && (
              <div className="text-sm text-gray-600 space-y-1">
                <p>Tipo: {fullManifest.backupType}</p>
                <p>Versão: {fullManifest.systemVersion}</p>
                <p>Criado em: {fullManifest.createdAt}</p>
                <p>Checksum: {fullManifest.hash}</p>
                <p>Tamanho: {formatBytes(fullManifest.size)}</p>
              </div>
            )}
            <div className="border rounded-lg p-3 bg-amber-50 text-sm text-amber-700">
              Essa ação irá sobrescrever dados. Confirmar?
            </div>
            <input
              type="text"
              placeholder="Digite RESTORE para confirmar"
              value={confirmTextFull}
              onChange={(e) => setConfirmTextFull(e.target.value)}
              className="border rounded-lg px-3 py-2 w-full"
            />
            <button
              className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 disabled:bg-gray-400"
              disabled={!fullFile || confirmTextFull !== 'RESTORE'}
              onClick={() =>
                fullFile &&
                restoreBackup(
                  fullFile,
                  'full',
                  confirmTextFull,
                  setFullRestoreId,
                  setFullLogs,
                  setErrorFull
                )
              }
            >
              Restaurar
            </button>
            {fullLogs.length > 0 && (
              <div className="text-xs bg-gray-100 rounded p-3 max-h-40 overflow-auto">
                {fullLogs.map((log, idx) => (
                  <div key={idx}>{log}</div>
                ))}
              </div>
            )}
            {errorFull && <p className="text-sm text-red-600">{errorFull}</p>}
          </div>
        </div>
      </section>
    </div>
  );
};

export default BackupRestore;
